import React, { useState, useEffect } from 'react';
import {
  DynamicContextProvider,
  DynamicWidget,
  useDynamicContext,
} from "@dynamic-labs/sdk-react-core";
import { SolanaWalletConnectors } from "@dynamic-labs/solana";
import './App.css';
import InvestmentModal from './components/InvestmentModal';
import ProjectSubmissionWizard from './components/ProjectSubmissionWizard';
import PublicLandingPage from './components/PublicLandingPage';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import AdminDashboard from './components/AdminDashboard';
import ProjectsShowcase from './components/ProjectsShowcase';

// Year 2+ — re-enable when blockchain layer is attorney-approved.
// Hides user-facing Web3 UI (wallet address display, external wallet connect, on-chain views,
// SOL/INDI currency toggle). Embedded Solana wallet creation via Dynamic.xyz remains active
// silently in the background — see DynamicContextProvider settings below.
const SHOW_WEB3_UI = false;

// Main App Component with Dynamic Provider
export default function App() {
  return (
    <DynamicContextProvider
settings={{
  environmentId: process.env.REACT_APP_DYNAMIC_ENVIRONMENT_ID,
  evmNetworks: [],
  appName: 'INDI Platform',
  
  walletsFilter: () => [],
  
  signWithEmailWalletName: 'INDI Account',
}}
    >
      <AppContent />
    </DynamicContextProvider>
  );
}

// App Content (inside Dynamic Context)
function AppContent() {
  const { user, authToken, getAuthToken, handleLogOut } = useDynamicContext();
  const [currentPage, setCurrentPage] = useState('home');
  const [currency, setCurrency] = useState('USD');
  const [exchangeRate] = useState({ SOL: 100, INDI: 1 });
  const [projects, setProjects] = useState([]);
  const [isWeb3Mode, setIsWeb3Mode] = useState(false);
  const [backendUser, setBackendUser] = useState(null);
  const [showPlatform, setShowPlatform] = useState(false);
  const [legalPage, setLegalPage] = useState(null); // 'privacy' | 'terms'
  const [showAdmin, setShowAdmin] = useState(false);

  // Hide wallet options in Dynamic modal using JavaScript
  useEffect(() => {
    const hideWallets = () => {
      const walletElements = document.querySelectorAll(
        '[class*="wallet"], [class*="Wallet"], [data-testid*="wallet"]'
      );
      walletElements.forEach(el => {
        if (el.textContent && !el.textContent.includes('email')) {
          el.style.display = 'none';
        }
      });
      const dividers = document.querySelectorAll('div, span, p');
      dividers.forEach(el => {
        if (el.textContent.trim() === 'Or' || el.textContent.trim() === 'or') {
          el.style.display = 'none';
        }
      });
    };
    hideWallets();
    const interval = setInterval(hideWallets, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Check URL for hidden platform access
  useEffect(() => {
    const checkRoute = () => {
      const hash = window.location.hash;
      if (hash === '#/platform') {
        setShowPlatform(true);
        setShowAdmin(false);
      } else if (hash === '#/admin') {
        setShowAdmin(true);
        setShowPlatform(false);
      }
    };
    checkRoute();
    window.addEventListener('hashchange', checkRoute);
    return () => window.removeEventListener('hashchange', checkRoute);
  }, []);

  // Sync user with backend after Dynamic login
  useEffect(() => {
    if (!user) {
      setBackendUser(null);
      return;
    }

    let attempts = 0;
    const maxAttempts = 5;

    const attemptSync = async () => {
      attempts++;

      let token = authToken;

      if (!token && typeof getAuthToken === 'function') {
        try {
          token = await getAuthToken();
        } catch (e) {
          console.log('getAuthToken not available, using user ID fallback');
        }
      }

      console.log(`🔄 Sync attempt ${attempts}:`, {
        hasUser: !!user,
        hasAuthToken: !!authToken,
        hasGetAuthToken: typeof getAuthToken === 'function',
        hasToken: !!token,
        userEmail: user?.email,
        userId: user?.userId
      });

      if (!token) {
        if (attempts < maxAttempts) {
          console.log(`⏳ Token not ready, retrying in 1 second... (${attempts}/${maxAttempts})`);
          setTimeout(attemptSync, 1000);
        } else {
          console.log('⚠️ No token after retries, attempting sync with user ID only...');
          syncWithoutToken();
        }
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/login`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();
        if (data.success) {
          setBackendUser(data.user);
          console.log('✅ User synced with backend:', data.user.email || data.user.dynamicUserId);
        } else {
          console.error('❌ Backend sync failed:', data.error);
        }
      } catch (error) {
        console.error('❌ Backend sync error:', error);
      }
    };

    const syncWithoutToken = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/sync`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              dynamicUserId: user.userId,
              email: user.email,
              verifiedCredentials: user.verifiedCredentials
            })
          }
        );
        const data = await response.json();
        if (data.success) {
          setBackendUser(data.user);
          console.log('✅ User synced (no-token fallback):', data.user.email || data.user.dynamicUserId);
        }
      } catch (error) {
        console.error('❌ No-token sync error:', error);
      }
    };

    setTimeout(attemptSync, 500);

  }, [user, authToken]);

  // Fetch projects (available to everyone, no auth required)
  useEffect(() => {
    fetchProjects();
  }, []);

  // Re-fetch projects when user logs in
  useEffect(() => {
    if (user) {
      fetchProjects();
    }
  }, [user]);

  const fetchProjects = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects`);
      const data = await response.json();
      if (data.success) {
        setProjects(data.projects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  // Currency conversion helper
  const formatCurrency = (amount) => {
    if (currency === 'USD') {
      return `$${amount.toLocaleString()}`;
    } else if (currency === 'SOL') {
      return `${(amount / exchangeRate.SOL).toFixed(2)} SOL`;
    } else if (currency === 'INDI') {
      return `${(amount * exchangeRate.INDI).toLocaleString()} INDI`;
    }
  };
// Admin dashboard (hidden route)
if (showAdmin) {
    return <AdminDashboard />;
  }

// Public landing page (default for all visitors)
if (legalPage === 'privacy') {
    return <PrivacyPolicy onBack={() => setLegalPage(null)} />;
  }
  if (legalPage === 'terms') {
    return <TermsOfService onBack={() => setLegalPage(null)} />;
  }
  if (!showPlatform) {
return (
      <PublicLandingPage
        onEnterPlatform={() => setShowPlatform(true)}
        onPrivacy={() => setLegalPage('privacy')}
        onTerms={() => setLegalPage('terms')}
      />
    );
  }

  return (
    <div className="app">
      {/* Header */}
      <header className="header">
        <div className="header-content">
          <div className="logo">
            <h1>INDI</h1>
            <span className="tagline">Independent Film Platform</span>
          </div>

<nav className="nav">
  <button 
    className={currentPage === 'home' ? 'active' : ''} 
    onClick={() => setCurrentPage('home')}>
    Home
  </button>
  <button 
    className={currentPage === 'projects' ? 'active' : ''} 
    onClick={() => setCurrentPage('projects')}>
    Projects
  </button>
  <button 
    className={currentPage === 'submit' ? 'active' : ''} 
    onClick={() => setCurrentPage('submit')}>
    Submit Project
  </button>
  {user && (
    <>
      <button 
        className={currentPage === 'dashboard' ? 'active' : ''} 
        onClick={() => setCurrentPage('dashboard')}>
        Dashboard
      </button>
      <button 
        className={currentPage === 'settings' ? 'active' : ''} 
        onClick={() => setCurrentPage('settings')}>
        ⚙️ Settings
      </button>
    </>
  )}
</nav>

          {user && SHOW_WEB3_UI && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <div
                onClick={() => setCurrentPage('settings')}
                style={{
                  padding: '0.5rem 1rem',
                  background: isWeb3Mode
                    ? 'rgba(163, 230, 53, 0.1)'
                    : 'rgba(6, 182, 212, 0.1)',
                  border: isWeb3Mode
                    ? '1px solid rgba(163, 230, 53, 0.3)'
                    : '1px solid rgba(6, 182, 212, 0.2)',
                  borderRadius: '10px',
                  color: isWeb3Mode ? '#A3E635' : '#22D3EE',
                  cursor: 'pointer',
                  fontFamily: "'Instrument Sans', system-ui, sans-serif",
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  transition: 'all 250ms ease',
                }}
                title="Click to toggle account mode"
              >
                {isWeb3Mode ? '🔗 Web3' : '💵 Standard'}
              </div>

              {isWeb3Mode && (
                <div className="currency-toggle">
                  <button
                    className={currency === 'USD' ? 'active' : ''}
                    onClick={() => setCurrency('USD')}>
                    USD
                  </button>
                  <button
                    className={currency === 'SOL' ? 'active' : ''}
                    onClick={() => setCurrency('SOL')}>
                    SOL
                  </button>
                  <button
                    className={currency === 'INDI' ? 'active' : ''}
                    onClick={() => setCurrency('INDI')}>
                    INDI
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="auth-section">
            {user ? (
              <button onClick={handleLogOut} className="logout-btn">
                Sign Out
              </button>
            ) : (
              <DynamicWidget />
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main-content">
        {!user && currentPage === 'home' && <LandingPage />}
        {user && currentPage === 'home' && <HomePage user={user} formatCurrency={formatCurrency} />}
        {currentPage === 'projects' && <ProjectsShowcase />}
        {currentPage === 'submit' && (
          !user ? (
            <div style={{
              maxWidth: '600px',
              margin: '4rem auto',
              textAlign: 'center',
              padding: '3rem 2rem',
              background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
              border: '1px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '16px',
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎬</div>
              <h2 style={{
                fontFamily: "'Outfit', system-ui, sans-serif",
                color: '#F1F5F9',
                fontSize: '1.5rem',
                fontWeight: '700',
                marginBottom: '0.75rem',
              }}>
                Ready to Submit Your Film?
              </h2>
              <p style={{
                fontFamily: "'Instrument Sans', system-ui, sans-serif",
                color: '#94A3B8',
                fontSize: '0.9375rem',
                lineHeight: '1.6',
                marginBottom: '2rem',
              }}>
                Sign in to submit your project to the INDI team for review. 
                Our comprehensive intake process ensures your project meets 
                SEC compliance requirements and is ready for investor funding.
              </p>
              <DynamicWidget />
            </div>
          ) : backendUser?.activeProjectId ? (
            <ProjectSubmissionWizard
              projectId={backendUser.activeProjectId}
              onComplete={() => {
                setBackendUser(prev => ({ ...prev, activeProjectId: null }));
                setCurrentPage('dashboard');
                fetchProjects();
              }}
              onClose={() => {
                setBackendUser(prev => ({ ...prev, activeProjectId: null }));
                setCurrentPage('projects');
              }}
            />
          ) : (
            <SubmitProjectPage
              authToken={authToken}
              backendUser={backendUser}
              onProjectCreated={(projectId) => {
                setBackendUser(prev => ({ ...prev, activeProjectId: projectId }));
              }}
            />
          )
        )}
        {currentPage === 'dashboard' && user && <DashboardPage user={user} backendUser={backendUser} />}
        {currentPage === 'settings' && user && (
          <SettingsPage 
            isWeb3Mode={isWeb3Mode} 
            setIsWeb3Mode={setIsWeb3Mode}
            currency={currency}
            setCurrency={setCurrency}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="footer">
        <p>© 2024 INDI Platform - Democratizing Film Funding</p>
        <div className="footer-links">
          <a href="#about">About</a>
          <a href="#how-it-works">How It Works</a>
          <a href="#faq">FAQ</a>
          <a href="#contact">Contact</a>
        </div>
      </footer>
    </div>
  );
}

// Landing Page (Not Authenticated)
function LandingPage() {
  const [countdownWord, setCountdownWord] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasCompleted, setHasCompleted] = useState(false);

  const handleMouseEnter = () => {
    if (!isAnimating && hasCompleted) {
      setHasCompleted(false);
    }
    
    if (!isAnimating && !hasCompleted) {
      setIsAnimating(true);
      const sequence = ['FUND', 'CREATE', 'DISTRIBUTE', 'WATCH', 'EARN', 'INDI'];
      let index = 0;
      
      const interval = setInterval(() => {
        if (index < sequence.length) {
          setCountdownWord(sequence[index]);
          index++;
        } else {
          clearInterval(interval);
          setTimeout(() => {
            setCountdownWord('');
            setIsAnimating(false);
            setHasCompleted(true);
          }, 5000);
        }
      }, 800);
    }
  };

  const handleMouseLeave = () => {
    if (hasCompleted) {
      setCountdownWord('');
      setIsAnimating(false);
      setHasCompleted(false);
    }
  };

  return (
    <div className="landing-page">
      <section className="hero">
        <div className="hero-content">
          <h2 className="hero-main-title">
            Empowering{' '}
            <span className="hero-gradient-text">
              Independent Filmmakers
            </span>
          </h2>
          <p className="hero-subtitle">
            Leveraging technology and community to streamline fundraising and distribution of curated film projects.
          </p>
        </div>

        <div className="film-projector-container">
          <svg width="900" height="500" viewBox="0 0 900 500"
            className={`projector-svg ${isAnimating ? 'animating' : ''}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}>
            <defs>
              <linearGradient id="lightBeam" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: '#06B6D4', stopOpacity: 0.8 }} />
                <stop offset="100%" style={{ stopColor: '#06B6D4', stopOpacity: 0.1 }} />
              </linearGradient>
              <radialGradient id="screenGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" style={{ stopColor: '#22D3EE', stopOpacity: 0.6 }} />
                <stop offset="100%" style={{ stopColor: '#06B6D4', stopOpacity: 0 }} />
              </radialGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
              <filter id="filmGrain">
                <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/>
                <feColorMatrix type="saturate" values="0"/>
                <feComponentTransfer>
                  <feFuncA type="discrete" tableValues="0 0 0 0.05 0"/>
                </feComponentTransfer>
                <feBlend mode="multiply" in2="SourceGraphic"/>
              </filter>
              <clipPath id="screenClip">
                <rect x="560" y="180" width="320" height="220" rx="6" />
              </clipPath>
            </defs>

            <g className="left-reel" transform="translate(-10, 0)">
              <g className="reel-rotate" style={{ transformOrigin: '80px 100px' }}>
                <circle cx="80" cy="100" r="60" fill="rgba(15, 23, 42, 0.6)" stroke="#06B6D4" strokeWidth="3" />
                <circle cx="80" cy="100" r="14" fill="rgba(6, 182, 212, 0.3)" stroke="#22D3EE" strokeWidth="2" />
                <circle cx="80" cy="63" r="16" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="2" />
                <circle cx="115.2" cy="90.2" r="16" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="2" />
                <circle cx="102" cy="133" r="16" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="2" />
                <circle cx="58" cy="133" r="16" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="2" />
                <circle cx="44.8" cy="90.2" r="16" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="2" />
                <line x1="91.1" y1="88.4" x2="128.8" y2="58.4" stroke="#22D3EE" strokeWidth="2.5" />
                <line x1="93.3" y1="107.7" x2="132.8" y2="123.3" stroke="#22D3EE" strokeWidth="2.5" />
                <line x1="80" y1="114" x2="80" y2="160" stroke="#22D3EE" strokeWidth="2.5" />
                <line x1="66.7" y1="107.7" x2="27.2" y2="123.3" stroke="#22D3EE" strokeWidth="2.5" />
                <line x1="68.9" y1="88.4" x2="31.2" y2="58.4" stroke="#22D3EE" strokeWidth="2.5" />
              </g>
            </g>

            <g className="right-reel" transform="translate(30, 0)">
              <g className="reel-rotate" style={{ transformOrigin: '220px 100px' }}>
                <circle cx="220" cy="100" r="90" fill="rgba(15, 23, 42, 0.6)" stroke="#06B6D4" strokeWidth="4" />
                <circle cx="220" cy="100" r="20" fill="rgba(6, 182, 212, 0.3)" stroke="#22D3EE" strokeWidth="3" />
                <circle cx="220" cy="45" r="24" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="3" />
                <circle cx="272.8" cy="90.8" r="24" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="3" />
                <circle cx="253" cy="150" r="24" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="3" />
                <circle cx="187" cy="150" r="24" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="3" />
                <circle cx="167.2" cy="90.8" r="24" fill="rgba(10, 10, 16, 0.9)" stroke="#06B6D4" strokeWidth="3" />
                <line x1="236.7" y1="82.6" x2="283.2" y2="37.6" stroke="#22D3EE" strokeWidth="3" />
                <line x1="239.9" y1="111.5" x2="307.3" y2="134.9" stroke="#22D3EE" strokeWidth="3" />
                <line x1="220" y1="120" x2="220" y2="190" stroke="#22D3EE" strokeWidth="3" />
                <line x1="200.1" y1="111.5" x2="132.7" y2="134.9" stroke="#22D3EE" strokeWidth="3" />
                <line x1="203.3" y1="82.6" x2="156.8" y2="37.6" stroke="#22D3EE" strokeWidth="3" />
              </g>
            </g>

            <path d="M 140 100 Q 180 100, 130 100" stroke="#22D3EE" strokeWidth="20" fill="none" opacity="0.4" />
            <rect x="135" y="90" width="5" height="6" fill="#0A0A10" opacity="0.8" />
            <rect x="135" y="104" width="5" height="6" fill="#0A0A10" opacity="0.8" />

            <g className="projector-body">
              <rect x="60" y="220" width="220" height="140" rx="6" fill="rgba(15, 23, 42, 0.85)" stroke="#06B6D4" strokeWidth="4" />
              <rect x="90" y="195" width="160" height="25" rx="4" fill="rgba(15, 23, 42, 0.9)" stroke="#06B6D4" strokeWidth="3" />
              <rect x="280" y="240" width="60" height="100" rx="4" fill="rgba(15, 23, 42, 0.9)" stroke="#06B6D4" strokeWidth="3" />
              <circle cx="340" cy="290" r="35" fill="rgba(15, 23, 42, 0.95)" stroke="#06B6D4" strokeWidth="3" />
              <circle cx="340" cy="290" r="28" className="lens-glow" fill="rgba(6, 182, 212, 0.2)" stroke="#06B6D4" strokeWidth="3" />
              <circle cx="340" cy="290" r="18" className="lens-inner" fill="rgba(6, 182, 212, 0.4)" stroke="#22D3EE" strokeWidth="2" />
              <rect x="80" y="240" width="35" height="10" rx="3" fill="#22D3EE" opacity="0.5" />
              <rect x="80" y="265" width="40" height="10" rx="3" fill="#22D3EE" opacity="0.5" />
              <rect x="80" y="290" width="35" height="10" rx="3" fill="#22D3EE" opacity="0.5" />
              <rect x="80" y="315" width="42" height="10" rx="3" fill="#22D3EE" opacity="0.5" />
              <circle cx="240" cy="250" r="6" fill="rgba(251, 191, 36, 0.3)" stroke="#22D3EE" strokeWidth="2" />
            </g>

            <g className="tripod" stroke="#06B6D4" strokeWidth="4" fill="none">
              <line x1="170" y1="360" x2="120" y2="460" />
              <line x1="170" y1="360" x2="220" y2="460" />
              <line x1="170" y1="360" x2="170" y2="460" />
              <line x1="110" y1="460" x2="230" y2="460" strokeWidth="5" />
            </g>

            <g className="light-beam">
              <polygon points="375,270 375,310 650,150 650,430" fill="url(#lightBeam)" opacity="0" />
            </g>

            <g className="projection-screen">
              <ellipse cx="720" cy="290" rx="180" ry="120" fill="url(#screenGlow)" className="screen-glow" opacity={isAnimating ? 1 : 0} />
              <rect x="560" y="180" width="320" height="220" rx="6" fill="rgba(240, 245, 255, 0.85)" opacity={isAnimating ? 1 : 0} style={{ transition: 'opacity 0.5s ease' }} />
              <g clipPath="url(#screenClip)">
                <rect x="560" y="180" width="320" height="220" rx="6" fill="rgba(0, 0, 0, 0.03)" opacity={isAnimating ? 1 : 0} style={{ transition: 'opacity 0.5s ease' }} filter="url(#filmGrain)" />
              </g>
              {countdownWord && countdownWord !== 'INDI' && (
                <g opacity={countdownWord ? 1 : 0} style={{ transition: 'opacity 0.3s ease' }}>
                  <circle cx="720" cy="290" r="75" fill="none" stroke="#1e293b" strokeWidth="4" />
                  <circle cx="720" cy="290" r="58" fill="none" stroke="#1e293b" strokeWidth="2" />
                  <text x="720" y="295" textAnchor="middle" dominantBaseline="middle" fill="#0f172a" fontSize={countdownWord === 'DISTRIBUTE' ? 30 : 42} fontWeight="900" fontFamily="Outfit" letterSpacing="2">{countdownWord}</text>
                </g>
              )}
              {countdownWord === 'INDI' && (
                <text x="720" y="295" textAnchor="middle" dominantBaseline="middle" fill="#0f172a" fontSize="70" fontWeight="900" fontFamily="Outfit" opacity={countdownWord === 'INDI' ? 1 : 0} style={{ transition: 'opacity 0.5s ease' }}>INDI</text>
              )}
            </g>
          </svg>
        </div>

        <div className="cta-section">
          <p className="cta-text">Sign in above to get started</p>
          <div className="cta-features">
            <span>✓ Submit projects</span>
            <span>✓ Vote on films</span>
            <span>✓ Invest & earn</span>
          </div>
        </div>
      </section>
    </div>
  );
}

// Home Page (Authenticated)
function HomePage({ user, formatCurrency }) {
  return (
    <div className="home-page">
      <h2>Welcome back, {user?.alias || user?.email || 'Filmmaker'}! 🎉</h2>
      
      <div className="dashboard-grid">
        <div className="stat-card">
          <h3>Active Projects</h3>
          <p className="stat-number">24</p>
          <span className="stat-label">Currently in voting</span>
        </div>
        
        <div className="stat-card">
          <h3>Funded This Month</h3>
          <p className="stat-number">{formatCurrency(2400000)}</p>
          <span className="stat-label">Across 12 projects</span>
        </div>
        
        <div className="stat-card">
          <h3>Your Investments</h3>
          <p className="stat-number">3</p>
          <span className="stat-label">Projects backed</span>
        </div>
      </div>

      <section className="quick-actions">
        <h3>Quick Actions</h3>
        <div className="action-buttons">
          <button className="action-btn primary">🎬 Submit a Project</button>
          <button className="action-btn">👀 Browse Projects</button>
          <button className="action-btn">🗳️ Vote on Projects</button>
        </div>
      </section>
    </div>
  );
}

// Projects Page
function ProjectsPage({ projects, formatCurrency }) {
  const [selectedProject, setSelectedProject] = useState(null);
  const { user, setShowAuthFlow } = useDynamicContext();
  const [showInvestModal, setShowInvestModal] = useState(false);

  const handleInvestClick = (project) => {
    if (!user) {
      setShowAuthFlow(true);
      return;
    }
    setShowInvestModal(true);
  };

  return (
    <div className="projects-page">
      <h2>All Projects</h2>
      
      <div className="filters">
        <button className="filter-btn active">All</button>
        <button className="filter-btn">Voting</button>
        <button className="filter-btn">Crowdfunding</button>
        <button className="filter-btn">Funded</button>
        <button className="filter-btn">In Production</button>
      </div>

      <div className="projects-grid">
        {projects.length === 0 ? (
          <div className="no-projects">
            <p>No projects yet. Be the first to submit one! 🎬</p>
          </div>
        ) : (
          projects.map(project => (
            <ProjectCard key={project._id} project={project} formatCurrency={formatCurrency} onViewDetails={() => setSelectedProject(project)} />
          ))
        )}
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div style={{
          position: 'fixed',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(6, 10, 19, 0.92)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 200,
          padding: '1rem',
        }} onClick={() => setSelectedProject(null)}>
          <div style={{
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto',
            background: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)',
            border: '1px solid rgba(6, 182, 212, 0.15)',
            borderRadius: '16px',
            padding: '2rem',
          }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
              <div>
                <span style={{
                  display: 'inline-block',
                  padding: '0.2rem 0.75rem',
                  background: selectedProject.status === 'crowdfunding' ? 'rgba(132, 204, 22, 0.15)' : 'rgba(6, 182, 212, 0.15)',
                  border: `1px solid ${selectedProject.status === 'crowdfunding' ? 'rgba(132, 204, 22, 0.3)' : 'rgba(6, 182, 212, 0.3)'}`,
                  borderRadius: '6px',
                  color: selectedProject.status === 'crowdfunding' ? '#84CC16' : '#06B6D4',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  marginBottom: '0.5rem',
                }}>{selectedProject.status}</span>
                <h2 style={{
                  fontFamily: "'Outfit', system-ui, sans-serif",
                  fontSize: '1.75rem',
                  fontWeight: '700',
                  color: '#F1F5F9',
                  marginBottom: '0.25rem',
                }}>{selectedProject.title}</h2>
                <p style={{ color: '#06B6D4', fontSize: '0.9375rem' }}>{selectedProject.genre?.join(', ')}</p>
              </div>
              <button onClick={() => setSelectedProject(null)} style={{
                background: 'none', border: 'none', color: '#64748B', fontSize: '1.5rem', cursor: 'pointer',
              }}>✕</button>
            </div>

            {/* Tagline */}
            {selectedProject.tagline && (
              <p style={{
                fontFamily: "'Instrument Sans', system-ui, sans-serif",
                fontSize: '1.0625rem',
                fontStyle: 'italic',
                color: '#94A3B8',
                marginBottom: '1.25rem',
                lineHeight: '1.6',
              }}>"{selectedProject.tagline}"</p>
            )}

            {/* Synopsis */}
            {selectedProject.synopsis && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ color: '#94A3B8', fontSize: '0.8125rem', fontWeight: '600', marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Synopsis</h4>
                <p style={{ color: '#B0B8C8', fontSize: '0.9375rem', lineHeight: '1.7' }}>{selectedProject.synopsis}</p>
              </div>
            )}

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}>
              <div style={{
                background: 'rgba(6, 182, 212, 0.06)',
                border: '1px solid rgba(6, 182, 212, 0.1)',
                borderRadius: '12px',
                padding: '1rem',
                textAlign: 'center',
              }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.25rem', color: '#22D3EE', fontWeight: '500' }}>
                  {formatCurrency(selectedProject.budget?.fundingGoal || 0)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.25rem' }}>Funding Goal</div>
              </div>
              <div style={{
                background: 'rgba(6, 182, 212, 0.06)',
                border: '1px solid rgba(6, 182, 212, 0.1)',
                borderRadius: '12px',
                padding: '1rem',
                textAlign: 'center',
              }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.25rem', color: '#22D3EE', fontWeight: '500' }}>
                  {formatCurrency(selectedProject.budget?.totalBudget || 0)}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.25rem' }}>Total Budget</div>
              </div>
              <div style={{
                background: 'rgba(6, 182, 212, 0.06)',
                border: '1px solid rgba(6, 182, 212, 0.1)',
                borderRadius: '12px',
                padding: '1rem',
                textAlign: 'center',
              }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.25rem', color: '#22D3EE', fontWeight: '500' }}>
                  {selectedProject.voting ? selectedProject.voting.upvotes - selectedProject.voting.downvotes : 0}
                </div>
                <div style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '0.25rem' }}>Votes</div>
              </div>
            </div>

            {/* Crowdfunding Progress (if applicable) */}
            {selectedProject.crowdfunding && selectedProject.status === 'crowdfunding' && (
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ color: '#94A3B8', fontSize: '0.8125rem' }}>
                    {formatCurrency(selectedProject.crowdfunding.amountRaised || 0)} raised
                  </span>
                  <span style={{ color: '#64748B', fontSize: '0.8125rem' }}>
                    {Math.round(((selectedProject.crowdfunding.amountRaised || 0) / (selectedProject.budget?.fundingGoal || 1)) * 100)}%
                  </span>
                </div>
                <div style={{
                  height: '6px',
                  background: 'rgba(6, 182, 212, 0.1)',
                  borderRadius: '3px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(100, ((selectedProject.crowdfunding.amountRaised || 0) / (selectedProject.budget?.fundingGoal || 1)) * 100)}%`,
                    background: 'linear-gradient(90deg, #06B6D4, #84CC16)',
                    borderRadius: '3px',
                    transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {selectedProject.status === 'crowdfunding' && (
                <button
                  onClick={() => handleInvestClick(selectedProject)}
                  style={{
                    flex: 1,
                    padding: '0.85rem',
                    background: 'linear-gradient(135deg, #84CC16 0%, #A3E635 100%)',
                    color: '#0F172A',
                    border: 'none',
                    borderRadius: '10px',
                    fontFamily: "'Instrument Sans', system-ui, sans-serif",
                    fontWeight: '700',
                    fontSize: '1rem',
                    cursor: 'pointer',
                  }}
                >
                  💰 Invest in This Project
                </button>
              )}
              <button
                onClick={() => setSelectedProject(null)}
                style={{
                  flex: selectedProject.status === 'crowdfunding' ? 0 : 1,
                  padding: '0.85rem 1.5rem',
                  background: 'rgba(6, 182, 212, 0.08)',
                  color: '#06B6D4',
                  border: '1px solid rgba(6, 182, 212, 0.2)',
                  borderRadius: '10px',
                  fontFamily: "'Instrument Sans', system-ui, sans-serif",
                  fontWeight: '600',
                  fontSize: '0.9375rem',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Investment Modal */}
      {showInvestModal && user && selectedProject && (
        <InvestmentModal
          project={selectedProject}
          onClose={() => setShowInvestModal(false)}
          onSuccess={() => {
            alert('Investment successful! 🎉');
            setShowInvestModal(false);
            setSelectedProject(null);
          }}
        />
      )}
    </div>
  );
}

// Demo Project Card
function DemoProjectCard({ title, genre, goal, status, votes, formatCurrency }) {
  const { user, setShowAuthFlow } = useDynamicContext();
  const [showInvestModal, setShowInvestModal] = useState(false);
  const goalAmount = parseInt(goal.replace(/[$,]/g, ''));
  
  const handleInvestClick = () => {
    if (!user) {
      setShowAuthFlow(true);
      return;
    }
    setShowInvestModal(true);
  };
  
  return (
    <>
      <div className="project-card">
        <div className="project-poster">
          <div className="poster-placeholder">🎬</div>
          <span className={`status-badge ${status.toLowerCase()}`}>{status}</span>
        </div>
        <div className="project-info">
          <h3>{title}</h3>
          <p className="genre">{genre}</p>
          <div className="project-stats">
            <div className="stat">
              <span className="label">Goal:</span>
              <span className="value">{formatCurrency(goalAmount)}</span>
            </div>
            <div className="stat">
              <span className="label">Votes:</span>
              <span className="value">{votes}</span>
            </div>
          </div>
          
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
            <button className="btn-primary" style={{ flex: 1 }}>
              View Details
            </button>
            {status === 'Crowdfunding' && (
              <button 
                onClick={handleInvestClick}
                className="btn-primary" 
                style={{ 
                  flex: 1,
                  background: 'linear-gradient(135deg, #84CC16 0%, #A3E635 100%)',
                  color: '#0F172A'
                }}>
                💰 Invest
              </button>
            )}
          </div>
        </div>
      </div>

      {showInvestModal && user && (
        <InvestmentModal
          project={{ 
            title, 
            _id: title.toLowerCase().replace(/\s/g, '-'),
            id: title.toLowerCase().replace(/\s/g, '-')
          }}
          onClose={() => setShowInvestModal(false)}
          onSuccess={() => {
            alert('Investment successful! 🎉');
            setShowInvestModal(false);
          }}
        />
      )}
    </>
  );
}

// Project Card Component
function ProjectCard({ project, formatCurrency, onViewDetails }) {
  return (
    <div className="project-card">
      <div className="project-poster">
        {project.poster ? (
          <img src={project.poster} alt={project.title} />
        ) : (
          <div className="poster-placeholder">🎬</div>
        )}
        <span className={`status-badge ${project.status}`}>{project.status}</span>
      </div>
      <div className="project-info">
        <h3>{project.title}</h3>
        <p className="genre">{project.genre?.join(', ')}</p>
        <div className="project-stats">
          <div className="stat">
            <span className="label">Goal:</span>
            <span className="value">{formatCurrency(project.budget?.fundingGoal || 0)}</span>
          </div>
          {project.voting && (
            <div className="stat">
              <span className="label">Votes:</span>
              <span className="value">{project.voting.upvotes - project.voting.downvotes}</span>
            </div>
          )}
        </div>
        <button className="btn-primary" onClick={onViewDetails}>View Details</button>
      </div>
    </div>
  );
}

// Submit Project Page
function SubmitProjectPage({ authToken, backendUser, onProjectCreated }) {
  const [formData, setFormData] = useState({
    title: '',
    tagline: '',
    genre: '',
    synopsis: '',
    fundingGoal: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

   // if (!backendUser || backendUser.kycStatus !== 'approved') {
    //  setMessage({
    //    type: 'error',
     //   text: 'KYC verification required before submitting projects. Please complete verification in Settings.'
    //  });
    //  return;
    // }

    setSubmitting(true);
    setMessage(null);

    try {
      console.log('DEBUG backendUser:', JSON.stringify(backendUser, null, 2));
      const response = await fetch(`${process.env.REACT_APP_API_URL}/projects/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: formData.title,
          tagline: formData.tagline,
          genre: [formData.genre],
          synopsis: formData.synopsis,
          fundingGoal: parseFloat(formData.fundingGoal),
          totalBudget: parseFloat(formData.fundingGoal),
          investorShare: 50,
          dynamicUserId: backendUser?.dynamicUserId
        })
      });

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: 'success',
          text: `Project "${data.project.title}" created! Project ID: ${data.project.projectId}`
        });
        if (onProjectCreated) onProjectCreated(data.project.projectId);
        setFormData({ title: '', tagline: '', genre: '', synopsis: '', fundingGoal: '' });
      } else {
        setMessage({ type: 'error', text: data.error || 'Failed to create project' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
      console.error('Submit project error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="submit-page">
      <h2>Submit Your Project</h2>
      <form onSubmit={handleSubmit} className="submit-form">
        <div className="form-group">
          <label>Project Title *</label>
          <input type="text" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="The Last Sunset" required />
        </div>
        <div className="form-group">
          <label>Tagline *</label>
          <input type="text" value={formData.tagline} onChange={(e) => setFormData({...formData, tagline: e.target.value})} placeholder="A story of hope in the darkest hour" required />
        </div>
        <div className="form-group">
          <label>Genre *</label>
          <select value={formData.genre} onChange={(e) => setFormData({...formData, genre: e.target.value})} required>
            <option value="">Select genre...</option>
            <option value="Drama">Drama</option>
            <option value="Comedy">Comedy</option>
            <option value="Thriller">Thriller</option>
            <option value="Horror">Horror</option>
            <option value="Sci-Fi">Sci-Fi</option>
            <option value="Documentary">Documentary</option>
          </select>
        </div>
        <div className="form-group">
          <label>Synopsis *</label>
          <textarea value={formData.synopsis} onChange={(e) => setFormData({...formData, synopsis: e.target.value})} placeholder="Describe your film..." rows="5" required />
        </div>
        <div className="form-group">
          <label>Funding Goal ($) *</label>
          <input type="number" value={formData.fundingGoal} onChange={(e) => setFormData({...formData, fundingGoal: e.target.value})} placeholder="500000" required />
        </div>
        {message && (
          <div style={{
            padding: '1rem',
            borderRadius: '10px',
            marginBottom: '1rem',
            background: message.type === 'success' ? 'rgba(163, 230, 53, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${message.type === 'success' ? 'rgba(163, 230, 53, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
            color: message.type === 'success' ? '#A3E635' : '#f87171',
            fontFamily: "'Instrument Sans', system-ui, sans-serif"
          }}>
            {message.text}
          </div>
        )}
        <button type="submit" className="btn-primary btn-large" disabled={submitting}>
          {submitting ? 'Submitting...' : 'Submit to INDI Team 🚀'}
        </button>
      </form>
    </div>
  );
}

// Dashboard Page
function DashboardPage({ user, backendUser }) {
  return (
    <div className="dashboard-page">
      <h2>Your Dashboard</h2>

      {/* Real user stats from backend */}
      {backendUser && (
        <div className="dashboard-stats" style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <div className="stat-card" style={{ background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.2)', borderRadius: '16px', padding: '1.5rem', minWidth: '150px' }}>
            <div style={{ fontFamily: "'JetBrains Mono', Menlo, monospace", fontSize: '1.5rem', fontWeight: '500', color: '#22D3EE' }}>
              ${(backendUser.totalInvested || 0).toLocaleString()}
            </div>
            <div style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: '0.8125rem', color: '#94A3B8', marginTop: '0.5rem' }}>Total Invested</div>
          </div>
          <div className="stat-card" style={{ background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.2)', borderRadius: '16px', padding: '1.5rem', minWidth: '150px' }}>
            <div style={{ fontFamily: "'JetBrains Mono', Menlo, monospace", fontSize: '1.5rem', fontWeight: '500', color: '#22D3EE' }}>
              ${(backendUser.totalRevenueReceived || 0).toLocaleString()}
            </div>
            <div style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: '0.8125rem', color: '#94A3B8', marginTop: '0.5rem' }}>Revenue Received</div>
          </div>
          <div className="stat-card" style={{ background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.2)', borderRadius: '16px', padding: '1.5rem', minWidth: '150px' }}>
            <div style={{ fontFamily: "'JetBrains Mono', Menlo, monospace", fontSize: '1.5rem', fontWeight: '500', color: '#22D3EE' }}>
              {backendUser.kycStatus === 'approved' ? '✅' : '⏳'}
            </div>
            <div style={{ fontFamily: "'Instrument Sans', system-ui, sans-serif", fontSize: '0.8125rem', color: '#94A3B8', marginTop: '0.5rem' }}>
              KYC: {backendUser.kycStatus?.replace('_', ' ') || 'Not Started'}
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-sections">
        <section className="dashboard-section">
          <h3>Your Projects</h3>
          <p>You haven't submitted any projects yet.</p>
          <button className="btn-primary">Submit Your First Project</button>
        </section>
        <section className="dashboard-section">
          <h3>Your Investments</h3>
          <p>You haven't invested in any projects yet.</p>
          <button className="btn-primary">Browse Projects</button>
        </section>
        <section className="dashboard-section">
          <h3>Your Votes</h3>
          <p>You haven't voted on any projects yet.</p>
          <button className="btn-primary">Start Voting</button>
        </section>
      </div>
    </div>
  );
}

// Settings Page
function SettingsPage({ isWeb3Mode, setIsWeb3Mode, currency, setCurrency }) {
  const { user, primaryWallet, setShowAuthFlow } = useDynamicContext();

const handleEnableWeb3 = () => {
    if (!isWeb3Mode) {
      setIsWeb3Mode(true);
      // Open Phantom wallet connection directly
      if (window.solana && window.solana.isPhantom) {
        window.solana.connect().then((resp) => {
          console.log('Wallet connected:', resp.publicKey.toString());
        }).catch((err) => {
          console.log('Wallet connection declined:', err);
        });
      } else {
        window.open('https://phantom.app/', '_blank');
      }
    }
  };

  const handleDisableWeb3 = () => {
    setIsWeb3Mode(false);
    if (primaryWallet) {
      primaryWallet.connector.disconnect();
    }
  };

  return (
    <div className="settings-page">
      <h2>Account Settings</h2>
      
      <div className="settings-sections">
        {SHOW_WEB3_UI && (
          <section className="settings-section">
            <h3>🔗 Web3 & Crypto Wallets</h3>
            <p className="section-description">
              Connect your crypto wallet to access Web3 features, invest in projects with cryptocurrency, and track your on-chain investments.
            </p>

            {!isWeb3Mode ? (
              <div className="web3-disabled">
                <div className="status-badge inactive">Web3 Mode: Disabled</div>
                <p>Currently using standard Web2 account with USD currency.</p>
                <button onClick={handleEnableWeb3} className="btn-primary" style={{ marginTop: '1rem' }}>
                  🔗 Enable Web3 & Connect Wallet
                </button>
              </div>
            ) : (
              <div className="web3-enabled">
                <div className="status-badge active">Web3 Mode: Enabled ✓</div>

                {primaryWallet ? (
                  <div className="wallet-info">
                    <p><strong>Connected Wallet:</strong></p>
                    <p className="wallet-address">
                      {primaryWallet.address.slice(0, 6)}...{primaryWallet.address.slice(-4)}
                    </p>
                    <p className="wallet-type">{primaryWallet.connector.name}</p>
                  </div>
                ) : (
                  <div>
                    <p>No wallet connected yet.</p>
                    <button onClick={() => {
                      if (window.solana && window.solana.isPhantom) {
                        window.solana.connect().then((resp) => {
                          console.log('Wallet connected:', resp.publicKey.toString());
                        }).catch((err) => {
                          console.log('Wallet connection declined:', err);
                        });
                      } else {
                        window.open('https://phantom.app/', '_blank');
                      }
                    }} className="btn-primary" style={{ marginTop: '1rem' }}>
                      Connect Wallet
                    </button>
                  </div>
                )}

                <button onClick={handleDisableWeb3} className="btn-secondary" style={{ marginTop: '1rem' }}>
                  Disable Web3 Mode
                </button>
              </div>
            )}
          </section>
        )}

        {SHOW_WEB3_UI && (
          <section className="settings-section">
            <h3>💰 Currency Display</h3>
            <p className="section-description">
              Choose your preferred currency for viewing project goals and investments.
            </p>

            <div className="currency-options">
              <button onClick={() => setCurrency('USD')} className={`currency-option ${currency === 'USD' ? 'active' : ''}`}>
                <span className="currency-icon">💵</span>
                <span className="currency-name">USD</span>
                {currency === 'USD' && <span className="check">✓</span>}
              </button>

              <button onClick={() => setCurrency('SOL')} className={`currency-option ${currency === 'SOL' ? 'active' : ''}`} disabled={!isWeb3Mode}>
                <span className="currency-icon">◎</span>
                <span className="currency-name">SOL</span>
                {currency === 'SOL' && <span className="check">✓</span>}
                {!isWeb3Mode && <span className="locked">🔒</span>}
              </button>

              <button onClick={() => setCurrency('INDI')} className={`currency-option ${currency === 'INDI' ? 'active' : ''}`} disabled={!isWeb3Mode}>
                <span className="currency-icon">🎬</span>
                <span className="currency-name">INDI</span>
                {currency === 'INDI' && <span className="check">✓</span>}
                {!isWeb3Mode && <span className="locked">🔒</span>}
              </button>
            </div>

            {!isWeb3Mode && (
              <p className="helper-text">Enable Web3 mode to use cryptocurrency displays</p>
            )}
          </section>
        )}

        <section className="settings-section">
          <h3>👤 Account Information</h3>
          <div className="account-info">
            {user?.alias && (
              <div className="info-row">
                <span className="label">Name:</span>
                <span className="value">{user.alias}</span>
              </div>
            )}
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{user?.email || '—'}</span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
